import { useState, useEffect, useRef, useMemo } from "react";
import http from "../../Api/http";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import toast from "react-hot-toast";
import Util from "../../Util";
import { motion } from "framer-motion";
import { Card, CardContent } from "../../components/card/Card";
import { Save } from "lucide-react";

const UserPermissions = () => {
    const { t, i18n } = useTranslation();

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const [permissions, setPermissions] = useState([]);
    const [permSearch, setPermSearch] = useState("");
    const [loading, setLoading] = useState(false);

    const drpUsers = useRef();

    // 🔍 load users
    useEffect(() => {
        const timeout = setTimeout(() => {
            loadUsers();
        }, 400);

        return () => clearTimeout(timeout);
    }, []);

    const loadUsers = async () => {
        const lang = i18n.language || "ar";

        const res = await http.getJsonAxios(
            http.actions.Users.GetUsers,
            { lang },
            t
        );

        if (res.status === 200) {
            setUsers(res.data);
        } else {
            setUsers([]);
        }
    };

    // 🎯 select user
    const handleSelectUser = async (user) => {
        try {
            setLoading(true);
            setSelectedUser(user);
            loadUserPermissions(user);
        }
        catch { }
        finally {
            setLoading(false);
        }
    };

    const loadUserPermissions = async (user = selectedUser) => {
        const res = await http.getJsonAxios(http.actions.Permissions.GetUserPermissions, { userId: user.value }, t);
        if (res.status === 200 && res.data.success) {
            setPermissions(res.data.obj || []);
        } else {
            setPermissions([]);
        }
    }

    // 🔄 toggle permission
    const togglePermission = (id) => {
        setPermissions(prev =>
            prev.map(p =>
                p.Id === id
                    ? { ...p, Granted: p.Granted ? false : true }
                    : p
            )
        );
    };

    // 🔍 filter permissions (search inside permissions)
    const filteredPermissions = useMemo(() => {
        return permissions.filter(p =>
            p.Name?.toLowerCase().includes(permSearch.toLowerCase())
        );
    }, [permissions, permSearch]);

    // 💾 save
    const handleSave = async () => {
        try {
            const selected = permissions.filter(p => p.Granted === true);
            const obj = { UserId: selectedUser.value, Permissions: selected };

            setLoading(true);
            const res = await http.postJsonAxios(http.actions.Permissions.SaveUserPermissions, obj, t);
            console.log(res)
            if (res.status === 200 && res.data.success) {
                toast.success(t("done"), Util.getSuccessToast());
                loadUserPermissions();
            }
        }
        catch { } finally {
            setLoading(false);
        }
    };

    const usersList = [
        { value: 0, label: t("select") },
        ...users.map(u => ({
            value: u.Id,
            label: u.Name
        }))
    ];

    return (
        <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto" dir={t('dir')}>
            {/* عنوان About */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
            >
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                    {t('userPermissions')}
                </h1>
                <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
            </motion.div>

            {/* 👤 USER SELECT */}
            <Card>
                <label>{t("user")}</label>
                <Select
                    ref={drpUsers}
                    value={selectedUser}
                    onChange={handleSelectUser}
                    options={usersList}
                    isSearchable={true}
                    placeholder={t('select')}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: 33,   // 👈 يقلل الارتفاع
                            height: 33,
                            fontSize: 13
                        }),

                        valueContainer: (base) => ({
                            ...base,
                            height: 32,
                            padding: "0 8px"
                        }),

                        input: (base) => ({
                            ...base,
                            margin: 0,
                            padding: 0
                        }),

                        indicatorsContainer: (base) => ({
                            ...base,
                            height: 32
                        })
                    }}
                />
            </Card>

            {/* 👤 Selected User */}
            {/* {selectedUser && (
                <div className="userInfo">
                    👤 <strong>{selectedUser.label}</strong>
                </div>
            )} */}

            {/* 🔐 Permissions */}
            <Card title={selectedUser ? selectedUser.label : ''}>
                {!selectedUser && (
                    <div className="empty">
                        {t("selectUserFirst")}
                    </div>
                )}

                {/* 🔍 Permissions Search */}
                {
                    selectedUser && (
                        <div className="searchBox">
                            <input
                                placeholder={`🔍 ${t('searchPermissions')}`}
                                value={permSearch}
                                onChange={(e) => setPermSearch(e.target.value)}
                                className="border px-2 py-1 rounded w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    )
                }
                {
                    selectedUser && loading && (<div className="loading">{t('loading')}</div>)
                }

                {
                    selectedUser && !loading && (
                        <div className="grid">
                            {filteredPermissions.map(p => (
                                <div key={p.Id} className="permCard">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={p.Granted}
                                            onChange={() => togglePermission(p.Id)}
                                        />
                                        {" "}
                                        {p.Name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )
                }
            </Card>

            {/* 💾 SAVE */}
            {/* {selectedUser && (
                <div className="footer">
                    <button onClick={handleSave}>
                        {t("save")}
                    </button>
                </div>
            )} */}
            {selectedUser && (
                <div className="footer">
                    <button
                        onClick={() => handleSave()}
                        className="bg-emerald-600 text-white px-4 py-2 rounded flex items-center gap-2 
                                                                  hover:bg-emerald-700 shadow-lg shadow-emerald-600/25"
                    >
                        <Save className="w-4 h-4" /> {t('save')}
                    </button>
                </div>
            )}

            {/* 🎨 STYLE */}
            <style>{`
                .userInfo {
                    margin: 10px 0;
                    font-size: 18px;
                }

                .searchBox input {
                    width: 100%;
                    padding: 10px;
                    border-radius: 8px;
                    border: 1px solid #ddd;
                    margin-bottom: 15px;
                }

                .grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                }

                .permCard {
                    background: #f7f7f7;
                    padding: 12px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .footer {
                    margin-top: 15px;
                    text-align: right;
                }

                .empty, .loading {
                    text-align: center;
                    padding: 40px;
                    color: #888;
                }

                /* 📱 MOBILE */
                @media (max-width: 768px) {
                    .grid {
                        grid-template-columns: 1fr;
                    }

                    .page {
                        padding: 10px;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserPermissions;